import React, { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import CustomTable from "components/CustomTable/CustomTable";
import { formatDate } from "helperFunctions/date";
import { CopyTwoTone } from "assets/icons";
import { copyText } from "helperFunctions/string";
import useMessage from "hooks/useMessage";
import { API_ENDPOINTS } from "utils/constants";
import centralApi from "services/centralApi";
import { useDispatch, useSelector } from "react-redux";
import { fetchTemplateById } from "redux/features/templates_/templateByIdSlice";
import { Dropdown, Spin } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface CampaignAuditTrailProps {
  did: string;
}

const CampaignAuditTrail: React.FC<CampaignAuditTrailProps> = ({ did }) => {
  const [campaignAuditTrail, setCampaignAuditTrail] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useMessage();
  const loginUser = useSelector((state: any) => state.loginUser);
  const { appId } = useSelector((state: any) => state.activeApp);
  const [rowTemplates, setRowTemplates] = useState<{ [key: string]: any }>({});
  const [loadingRows, setLoadingRows] = useState<{ [key: string]: boolean }>(
    {}
  );

  const fetchedDids = useRef<Set<string>>(new Set());
  const dispatch = useDispatch();
  const handleHover = async (tid: string) => {
    if (!tid || rowTemplates[tid]) return; // already fetched

    setLoadingRows((prev) => ({ ...prev, [tid]: true }));
    try {
      const res = await dispatch(
        fetchTemplateById({
          app_id: appId,
          api_key: loginUser.data.api_key,
          template_id: tid,
        })
      ).unwrap();
      setRowTemplates((prev) => ({ ...prev, [tid]: res }));
    } catch (error) {
      showError(t("somethingWentWrongLabel"));
    } finally {
      setLoadingRows((prev) => ({ ...prev, [tid]: false }));
    }
  };

  const fetchCampaignAuditTrail = async () => {
    try {
      setLoading(true);

      const payload = {
        api_key: loginUser?.data?.api_key,
        app_id: appId,
        did: did,
      };

      const response = await centralApi(
        "GET",
        API_ENDPOINTS.GET_CAMPAIGN_AUDIT_TRAIL,
        null,
        payload
      );

      if (response) {
        setCampaignAuditTrail(response?.campaigns || []);
      }
    } catch (error) {
      showError(t("somethingWentWrongLabel"));
    } finally {
      setLoading(false);
    }
  };

  const handleTextCopy = async (text: string) => {
    const res = await copyText(text);
    showSuccess(res.message);
  };

  const columns: any = [
    {
      title: t("campaignLabel"),
      key: "name",
      render: (details: any) => {
        const tid = details.tid;
        const template = rowTemplates[tid]?.template;
        const loadingTemplate = loadingRows[tid];
        const items = [
          {
            key: "1",
            label: loadingTemplate ? (
              <Spin size="small" />
            ) : template ? (
              <div>
                {template.notificationHeader && (
                  <div>
                    <b>Header:</b> {template.notificationHeader}
                  </div>
                )}
                {template.notificationDescription && (
                  <div>
                    <b>{t("descriptionLabel")}:</b> {template.notificationDescription}
                  </div>
                )}
                {template.message && (
                  <div>
                    <b>{t("messageLabel")}:</b> {template.message}
                  </div>
                )}
                {template.title && (
                  <div>
                    <b>{t("titleLabel")}:</b> {template.title}
                  </div>
                )}
                {template.headerTitle && (
                  <div>
                   <b>{t("titleLabel")}:</b> {template.headerTitle}
                  </div>
                )}
                {template.description && (
                  <div>
                   <b>{t("titleLabel")}:</b> {template.description}
                  </div>
                )}
              </div>
            ) : (
              <span>{t("noDataLabel")}</span>
            ),
          },
        ];

        return (
          <div className="d-flex justify-content-between align-items-start" >
          <div className="d-flex flex-column">
            {details.name}
            <span className="text-disabled d-flex align-items-center">
              {t("IdLabel")}: {details.campId}
              <CopyTwoTone className="ms-2 cursor-pointer "onClick={() => handleTextCopy(details._id)} />
            </span>
          </div>
          
          <Dropdown
            menu={{ items }}
            className="mt-2"
            trigger={["hover"]}
            onOpenChange={(open) => {
              if (open) handleHover(tid);
            }}
          >
            <InfoCircleOutlined />
          </Dropdown>
        </div>
        );
      },
    },
    {
      title: t("typeLabel"),
      dataIndex: "type",
      key: "type",
    },
    {
      title: t("campaignAuditTrailLabel"),
      dataIndex: "receivedTo",
      key: "receivedTo",
      className: "text-center",
      render: (value: any) => (value ? t("successLabel") : t("failedLabel")),
    },

    {
      title: t("sentAtLabel"),
      dataIndex: "receivedAt",
      key: "receivedAt",
      className: "text-center",
      render: (sd: number | null) => (
        <div>
          {sd ? formatDate(sd, "DD-MM-YYYY hh:mm:ss A") : "--"}
        </div>
      ),
      
    },
  ];

  useEffect(() => {
    // Only fetch if this DID hasn't been fetched yet
    if (
      did &&
      loginUser.data?.api_key &&
      appId &&
      !fetchedDids.current.has(did)
    ) {
      fetchedDids.current.add(did);

      fetchCampaignAuditTrail();
    }
  }, [did, appId, loginUser.api_key]);

  return (
    <>
      <div className="campaign-audit-trail-main-container">
        <h6 className="m-2">{t("campaignAuditTrailLabel")}</h6>
        <CustomTable
          dataSource={campaignAuditTrail || []} // always pass an array
          columns={columns}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={{
            pageSize: 5,   
          }}
        />
      </div>
    </>
  );
};

export default React.memo(CampaignAuditTrail);
